import { useState, useRef, useEffect } from 'react';
import { ArrowUpDown } from 'lucide-react';
import { clsx } from 'clsx';

interface SortOption {
  value: string;
  label: string;
}

interface SortMenuProps {
  options: SortOption[];
  selected: string;
  order: 'asc' | 'desc';
  onSort: (field: string) => void;
  onToggleOrder: () => void;
}

export function SortMenu({ options, selected, order, onSort, onToggleOrder }: SortMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-2 bg-surface-secondary border border-border rounded-lg text-xs font-medium text-text-secondary hover:bg-surface-tertiary transition-colors"
      >
        <ArrowUpDown size={14} />
        <span className="hidden sm:inline">Ordenar</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 bg-surface border border-border rounded-xl shadow-lg py-1 min-w-[160px] z-20 animate-scale-in">
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { onSort(opt.value); setOpen(false); }}
              className={clsx(
                'w-full text-left px-3 py-2 text-xs transition-colors',
                selected === opt.value
                  ? 'bg-brand-primary/10 text-brand-primary font-medium'
                  : 'text-text-secondary hover:bg-surface-secondary'
              )}
            >
              {opt.label}
            </button>
          ))}
          <hr className="my-1 border-border" />
          <button
            onClick={() => { onToggleOrder(); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-xs text-text-secondary hover:bg-surface-secondary"
          >
            {order === 'asc' ? '↑ Ascendente → ↓ Descendente' : '↓ Descendente → ↑ Ascendente'}
          </button>
        </div>
      )}
    </div>
  );
}
